import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from 'react-phone-number-input';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import BottomDrawer, { DrawerButtonsWrapper } from '../Drawer/Drawer';
import Button from '../Button/Button';
import PhoneNumberField from '../InputFields/PhoneNumberField';
import { InputError } from '../InputFields/Components/InputComponents';
import {
  enteredPhoneNumberValidatorAndFormatter,
  getIso2FromIso3CountryCode,
} from '../../util/helpers/phoneNumberHelpers';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';

const ApiError = styled.p`
  color: ${themeColors.colorError};
  font-size: ${pxToRem(13)};
  margin-top: ${pxToRem(8)};
`;

const FieldLabel = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
`;

const PhoneFieldWrapper = styled.div`
  ${InputError} {
    font-size: 14px;
    line-height: 140%;
  }

  /* Attach the country picker to the phone input and open it directly above
     the field (drop-up). The drawer is rendered with overflowVisible so the
     list can extend up out of the small drawer instead of being clipped. */
  && .intl-tel-input .country-list {
    position: absolute;
    top: auto !important;
    bottom: 100%;
    left: 0;
    right: 0;
    width: 100%;
    /* ~% of the parent modal height (modal is roughly viewport-height):
       40% on desktop, 65% on mobile (see the media query below). */
    max-height: 40vh;
    overflow-y: auto;
    z-index: 1060;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    && .intl-tel-input input {
      height: 38px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      line-height: 140%;
    }

    && .intl-tel-input .country-list {
      max-height: 65vh;
    }
  }
`;

const ButtonsRow = styled(DrawerButtonsWrapper)`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    && {
      grid-template-columns: 1fr 1fr;
    }

    button {
      height: 44px;
    }
  }
`;

const ChangeMobileNumber = ({
  open,
  setOpen,
  onPhoneSaved,
  currentPhone,
  currentIso3CountryCode,
}) => {
  const { t } = useTranslation();
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const selectedCountryRef = useRef(null);
  const defaultCountry = getIso2FromIso3CountryCode(currentIso3CountryCode);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (values, { setFieldError, setStatus }) => {
    const { NewPhoneNumber } = values;

    // An empty number must fail validation, not silently close the drawer
    // without saving.
    if (!NewPhoneNumber) {
      setFieldError(
        'NewPhoneNumber',
        t('validationSchema.general.phoneNumberRequired'),
      );
      return;
    }

    // Accept formatted numbers like "(215) 321-2036" by stripping standard
    // phone formatting (spaces, parens, dashes, dots) before the validation.
    const sanitizedNumber = NewPhoneNumber.replace(/[\s()\-.]/g, '');

    const isOnlyNumbers =
      sanitizedNumber.replace('+', '').match(/^[0-9]+$/) != null;

    if (!isOnlyNumbers && sanitizedNumber.length < 15) {
      setFieldError(
        'NewPhoneNumber',
        t('validationSchema.general.phoneNumberOnlyNumbers'),
      );
      return;
    }

    let phoneNumberJsonParsed;
    try {
      phoneNumberJsonParsed = JSON.parse(sanitizedNumber.replace('+', ''));
    } catch {
      setFieldError(
        'NewPhoneNumber',
        t('validationSchema.general.phoneNumberOnlyNumbers'),
      );
      return;
    }

    if (!isPossiblePhoneNumber(`+${phoneNumberJsonParsed}`)) {
      setFieldError(
        'NewPhoneNumber',
        t('validationSchema.general.phoneNumberNotValidLength'),
      );
      return;
    }

    if (!isValidPhoneNumber(`+${phoneNumberJsonParsed}`)) {
      setFieldError(
        'NewPhoneNumber',
        t('validationSchema.general.phoneNumberNotValidFormat'),
      );
      return;
    }

    const phoneNumber = enteredPhoneNumberValidatorAndFormatter(
      phoneNumberJsonParsed,
      (_, msg) => setFieldError('NewPhoneNumber', msg),
      selectedISOCodesListToIgnoreAreaCode,
      selectedCountryRef.current?.iso2,
    );

    if (!phoneNumber) {
      setFieldError(
        'NewPhoneNumber',
        t('validationSchema.general.phoneNumberNotValidFormat'),
      );
      return;
    }

    setStatus(null);
    onPhoneSaved(phoneNumber);
    setOpen(false);
  };

  return (
    <BottomDrawer
      isOpen={open}
      onClose={handleClose}
      title={t('gtt.changeMobileNumber.title')}
      overflowVisible
      priceAlertModalTitleCss={`font-size: 14px; line-height: 100%; font-family: ${variables.family.goldSilver};`}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={{ NewPhoneNumber: currentPhone || '' }}
        enableReinitialize
      >
        {({ setFieldValue, status }) => (
          <Form>
            <FieldLabel>{t('gtt.changeMobileNumber.subtitle')}</FieldLabel>
            <PhoneFieldWrapper>
              <PhoneNumberField
                name="NewPhoneNumber"
                // Keep the country list inline (drop-up) on mobile too, instead
                // of the library's body-portaled fullscreen which our scoped
                // styling can't reach.
                useMobileFullscreenDropdown={false}
                defaultCountry={defaultCountry}
                // On country change, clear the field so the selected country's
                // example number shows as the placeholder (matches registration).
                clearOnCountryChange
                onPhoneChange={(phoneNum, country) => {
                  selectedCountryRef.current = country;
                  setFieldValue('NewPhoneNumber', phoneNum);
                }}
              />
            </PhoneFieldWrapper>
            {status && <ApiError>{status}</ApiError>}

            <ButtonsRow style={{ marginTop: 16 }}>
              <Button
                variant="primaryOutlined"
                textTransform="none"
                type="button"
                onClick={handleClose}
                data-cy="button-change-mobile-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                textTransform="none"
                type="submit"
                data-cy="button-change-mobile-save"
              >
                {t('common.save')}
              </Button>
            </ButtonsRow>
          </Form>
        )}
      </Formik>
    </BottomDrawer>
  );
};

ChangeMobileNumber.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  onPhoneSaved: PropTypes.func,
  currentPhone: PropTypes.string,
  currentIso3CountryCode: PropTypes.string,
};

export default ChangeMobileNumber;
